import { useMemo } from 'react';
import { Extension, EditorSelection, SelectionRange } from '@codemirror/state';
import { keymap, EditorView } from '@codemirror/view';

/**
 * Select the next occurrence of the current selection (Cmd+D / Ctrl+D)
 */
function selectNextOccurrence(view: EditorView): boolean {
  const { state } = view;
  const { doc, selection } = state;
  const mainRange = selection.main;

  // If nothing is selected, select the word at cursor
  if (mainRange.empty) {
    const word = view.state.wordAt(mainRange.head);
    if (word) {
      view.dispatch({
        selection: EditorSelection.single(word.from, word.to),
      });
    }
    return true;
  }

  // Get the selected text
  const selectedText = doc.sliceString(mainRange.from, mainRange.to);
  if (!selectedText) return true;

  // Find the next occurrence after all current selections
  const existingRanges = selection.ranges;
  const lastRange = existingRanges[existingRanges.length - 1];
  const searchStart = lastRange.to;

  // Search from after the last selection to end of document
  let nextFrom = doc.toString().indexOf(selectedText, searchStart);

  // If not found, wrap around to start of document
  if (nextFrom === -1) {
    nextFrom = doc.toString().indexOf(selectedText, 0);
  }

  // If found and not already selected, add a new selection
  if (nextFrom !== -1) {
    const nextTo = nextFrom + selectedText.length;

    // Check if this range is already selected
    const alreadySelected = existingRanges.some(
      (r) => r.from === nextFrom && r.to === nextTo
    );

    if (!alreadySelected) {
      const newRanges: SelectionRange[] = [
        ...existingRanges,
        EditorSelection.range(nextFrom, nextTo),
      ];

      view.dispatch({
        selection: EditorSelection.create(newRanges, newRanges.length - 1),
        scrollIntoView: true,
      });
    }
  }

  return true;
}

/**
 * Select all occurrences of the current selection (Cmd+Shift+L / Ctrl+Shift+L)
 */
function selectAllOccurrences(view: EditorView): boolean {
  const { state } = view;
  const { doc, selection } = state;
  const mainRange = selection.main;

  // If nothing is selected, select the word at cursor first
  if (mainRange.empty) {
    const word = view.state.wordAt(mainRange.head);
    if (word) {
      view.dispatch({
        selection: EditorSelection.single(word.from, word.to),
      });
    }
    return true;
  }

  const selectedText = doc.sliceString(mainRange.from, mainRange.to);
  if (!selectedText) return true;

  const text = doc.toString();
  const ranges: SelectionRange[] = [];
  let pos = 0;

  while ((pos = text.indexOf(selectedText, pos)) !== -1) {
    ranges.push(EditorSelection.range(pos, pos + selectedText.length));
    pos += selectedText.length;
  }

  if (ranges.length > 0) {
    view.dispatch({
      selection: EditorSelection.create(ranges, 0),
    });
  }

  return true;
}

/**
 * Add cursor above (Cmd+Alt+Up / Ctrl+Alt+Up)
 */
function addCursorAbove(view: EditorView): boolean {
  const { state } = view;
  const { selection, doc } = state;
  const ranges: SelectionRange[] = [...selection.ranges];

  for (const range of selection.ranges) {
    const line = doc.lineAt(range.head);
    if (line.number > 1) {
      const prevLine = doc.line(line.number - 1);
      const column = Math.min(range.head - line.from, prevLine.length);
      const newHead = prevLine.from + column;
      ranges.push(EditorSelection.cursor(newHead));
    }
  }

  view.dispatch({
    selection: EditorSelection.create(ranges, ranges.length - 1),
    scrollIntoView: true,
  });

  return true;
}

/**
 * Add cursor below (Cmd+Alt+Down / Ctrl+Alt+Down)
 */
function addCursorBelow(view: EditorView): boolean {
  const { state } = view;
  const { selection, doc } = state;
  const ranges: SelectionRange[] = [...selection.ranges];

  for (const range of selection.ranges) {
    const line = doc.lineAt(range.head);
    if (line.number < doc.lines) {
      const nextLine = doc.line(line.number + 1);
      const column = Math.min(range.head - line.from, nextLine.length);
      const newHead = nextLine.from + column;
      ranges.push(EditorSelection.cursor(newHead));
    }
  }

  view.dispatch({
    selection: EditorSelection.create(ranges, ranges.length - 1),
    scrollIntoView: true,
  });

  return true;
}

/**
 * Move line up (Alt+Up)
 */
function moveLineUp(view: EditorView): boolean {
  const { state } = view;
  const { doc, selection } = state;
  const line = doc.lineAt(selection.main.head);

  if (line.number <= 1) return true;

  const prevLine = doc.line(line.number - 1);
  const lineText = doc.sliceString(line.from, line.to);
  const prevLineText = doc.sliceString(prevLine.from, prevLine.to);

  // Calculate new cursor position
  const cursorOffset = selection.main.head - line.from;
  const newCursorPos = prevLine.from + Math.min(cursorOffset, lineText.length);

  view.dispatch({
    changes: [
      { from: prevLine.from, to: line.to, insert: lineText + '\n' + prevLineText },
    ],
    selection: EditorSelection.cursor(newCursorPos),
    scrollIntoView: true,
  });

  return true;
}

/**
 * Move line down (Alt+Down)
 */
function moveLineDown(view: EditorView): boolean {
  const { state } = view;
  const { doc, selection } = state;
  const line = doc.lineAt(selection.main.head);

  if (line.number >= doc.lines) return true;

  const nextLine = doc.line(line.number + 1);
  const lineText = doc.sliceString(line.from, line.to);
  const nextLineText = doc.sliceString(nextLine.from, nextLine.to);

  // Calculate new cursor position
  const cursorOffset = selection.main.head - line.from;
  const newCursorPos = line.from + nextLineText.length + 1 + Math.min(cursorOffset, lineText.length);

  view.dispatch({
    changes: [
      { from: line.from, to: nextLine.to, insert: nextLineText + '\n' + lineText },
    ],
    selection: EditorSelection.cursor(newCursorPos),
    scrollIntoView: true,
  });

  return true;
}

/**
 * Duplicate line (Cmd+Shift+D / Ctrl+Shift+D)
 */
function duplicateLine(view: EditorView): boolean {
  const { state } = view;
  const { doc, selection } = state;
  const line = doc.lineAt(selection.main.head);
  const lineText = doc.sliceString(line.from, line.to);

  // Calculate new cursor position (same column on duplicated line)
  const cursorOffset = selection.main.head - line.from;
  const newCursorPos = line.to + 1 + cursorOffset;

  view.dispatch({
    changes: [{ from: line.to, insert: '\n' + lineText }],
    selection: EditorSelection.cursor(newCursorPos),
    scrollIntoView: true,
  });

  return true;
}

export function useMultiCursor() {
  const multiCursorExtension = useMemo<Extension>(() => {
    return keymap.of([
      // Cmd+D / Ctrl+D - Select next occurrence
      {
        key: 'Mod-d',
        run: selectNextOccurrence,
        preventDefault: true,
      },
      // Cmd+Shift+L / Ctrl+Shift+L - Select all occurrences
      {
        key: 'Mod-Shift-l',
        run: selectAllOccurrences,
        preventDefault: true,
      },
      // Cmd+Alt+Up / Ctrl+Alt+Up - Add cursor above
      {
        key: 'Mod-Alt-ArrowUp',
        run: addCursorAbove,
        preventDefault: true,
      },
      // Cmd+Alt+Down / Ctrl+Alt+Down - Add cursor below
      {
        key: 'Mod-Alt-ArrowDown',
        run: addCursorBelow,
        preventDefault: true,
      },
      // Alt+Up - Move line up
      {
        key: 'Alt-ArrowUp',
        run: moveLineUp,
        preventDefault: true,
      },
      // Alt+Down - Move line down
      {
        key: 'Alt-ArrowDown',
        run: moveLineDown,
        preventDefault: true,
      },
      // Cmd+Shift+D / Ctrl+Shift+D - Duplicate line
      {
        key: 'Mod-Shift-d',
        run: duplicateLine,
        preventDefault: true,
      },
    ]);
  }, []);

  return { multiCursorExtension };
}
